// @ts-ignore
import { useEffect } from "react";

import { useIntl } from "react-intl";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import {
    AnchorPageRelayQuery,
    shapeAnchorContentfulResponse
} from "src/contentful/graphql/anchor-page";

import { MeResponse } from "@atlassiansox/asc-stargate";

import { anchorPageRelayQuery } from "@/__generated_contentful__/anchorPageRelayQuery.graphql";
import { CarouselProps } from "@/components/confluence/carousel/src/carousel";
import { useLogMeInD0to6 } from "@/components/confluence/hooks/use-log-me-in-d0to6.tsx";
import { AvailableProductsResponse } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";
import { PaidHeroCarousel } from "@/patterns/paid-hero/src/paid-hero-carousel";
import HeroSection from "@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero";
import { BanditsProvider } from "@/utils/anchor-queries/bandits-components";
import {
    AnchorContentsResponse,
    PaidLandingPersonaTypes
} from "@/utils/anchor-queries/get-anchor-contents";
import {
    BANDITS_PRODUCT,
    BANDITS_USE_CASE
} from "@/utils/anchor-queries/get-bandits-contents";
import { experiments } from "@/utils/experiment/manifest";
import { useExperiment } from "@/utils/experiment/use-experiment";
import { useExperimentExposure } from "@/utils/experiment/use-experiment-exposure";
import { FeatureExposed } from "@/utils/feature-exposed/use-feature-exposed";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { ConfluenceSoftwarePageProps } from "./confluence-software-page";
import { carouselHeroMessages } from "./messages/carousel-hero.messages";
import { ConfluencePagePaidLpConflAnchor } from "./paid-lp-confl-anchor.tsx";
import { isTargetedSubAnchor } from "./subAnchors/subAnchor-util.tsx";

export interface ConfluencePageAnchorLpConfCollaborationRelayProps
    extends ConfluenceSoftwarePageProps {
    anchorContents?: AnchorContentsResponse | null;
    persona?: string;
    availableProducts: AvailableProductsResponse | null;
    userProfile: MeResponse | null;
    queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}
export const ConfluenceSoftwarePageVariantAnchorLpConfCollaboration = (
    pageProps: ConfluencePageAnchorLpConfCollaborationRelayProps
) => {
    const intl = useIntl();
    const [exposureSubAnchorFired, fireSubAnchorExposure] =
        useExperimentExposure(experiments.cfind4782LandingPageSubAnchorV3);
    useEffect(() => {
        if (
            isTargetedSubAnchor(pageProps.subAnchor) &&
            !exposureSubAnchorFired
        ) {
            fireSubAnchorExposure();
        }
    }, [exposureSubAnchorFired, fireSubAnchorExposure]);
    const gwpAJContextualBanditsConfluenceSubAnchor = useExperiment(
        experiments.gwpAJContextualBanditsConfluenceSubAnchor
    );
    const isCBSubAnchorExperimentEnabled =
        gwpAJContextualBanditsConfluenceSubAnchor.type === "enrolled" &&
        gwpAJContextualBanditsConfluenceSubAnchor.value;

    const data = usePreloadedQuery(
        AnchorPageRelayQuery,
        pageProps.queryRef ||
            (null as unknown as PreloadedQuery<anchorPageRelayQuery>)
    );
    pageProps = Object.assign({}, pageProps, {
        anchorContents: shapeAnchorContentfulResponse(data)
    });

    const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6(
        {
            availableProducts: pageProps.availableProducts,
            userProfile: pageProps.userProfile
        }
    );

    const gwp9286ConfluenceAnchorUpdates = checkFeatureFlagGate(
        featureFlags.gwp9286ConfluenceAnchorUpdates
    );

    const carouselSlidesOriginal = [
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Collaboration.get-on-the-same-page.title",
                defaultMessage: "Get on the same page"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Collaboration.get-on-the-same-page.body",
                defaultMessage:
                    "Plan projects, take notes, and brainstorm – all in one place."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/1e81unagi717g13fj44qtgpk0h8r0537.webp",
            imgAltText: "A Confluence page being edited"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Collaboration.collaborate-live-across-time-zones.title",
                defaultMessage: "Collaborate live across time zones"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Collaboration.collaborate-live-across-time-zones.body",
                defaultMessage:
                    "Move work forward with real-time editing and inline comments."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/i0011ky0775062fk18784clr4dcxd5wg.webp",
            imgAltText:
                "A Confluence page with multiple teammates editing and leaving comments"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Collaboration.find-what-you-need.title",
                defaultMessage: "Find what you need"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Collaboration.find-what-you-need.body",
                defaultMessage:
                    "Stay organized in dedicated workspaces, equipped with AI-powered search."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/fpd20037c363n21cfmh45nc5m50co161.webp",
            imgAltText: "Organized workspace in Confluence"
        }
    ];

    const carouselSlidesGwp9286Updates = [
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.CollaborationGwp9286Updates.turn-ideas-into-action.title",
                defaultMessage: "Turn ideas into action"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.CollaborationGwp9286Updates.turn-ideas-into-action.body",
                defaultMessage:
                    "Help your team think bigger, move faster, and capture ever spark."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/2p1rm3keie5h48ed6w7kag6u7m418264.webp",
            imgAltText: "Marketing brainstorming whiteboard"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.CollaborationGwp9286Updates.from-prompt-to-draft.title",
                defaultMessage: "From prompt to draft, in seconds"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.CollaborationGwp9286Updates.from-prompt-to-draft.body",
                defaultMessage:
                    "Spend less time starting from scratch and more time refining ideas with AI."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/37xpd5m0sv6yf55n24701d48q5uf2ut7.webp",
            imgAltText:
                "Screenshot of using AI to create a project plan document"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.CollaborationGwp9286Updates.stay-in-sync.title",
                defaultMessage: "Stay in sync"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.CollaborationGwp9286Updates.stay-in-sync.body",
                defaultMessage:
                    "Keep your team aligned from project kickoff to project completion."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/8v31m8aisl4ty5cg1xf0nuhjdfnqmk37.webp",
            imgAltText:
                "Screenshot of Rovo providing a summary of what changed on the page"
        }
    ];

    const carouselSlides = gwp9286ConfluenceAnchorUpdates
        ? carouselSlidesGwp9286Updates
        : carouselSlidesOriginal;

    const carouselHeroProps: CarouselProps = {
        "aria-label": intl.formatMessage(carouselHeroMessages.ariaLabel),
        "id": "confluence-collaboration-carousel",
        "slides": carouselSlides,
        "usePlainContainer": gwp9286ConfluenceAnchorUpdates
    };

    const pageId = "anchor-lp-conf-collaboration";

    const isLoggedIn = !!userProfileName;
    let customHeroSection;

    const getLiftShiftHeroSectionBrandUpdate = () => {
        return (
            <PaidHeroCarousel
                pageId={pageId}
                embeddedSignup={{ bundle: "confluence" }}
                carouselSlides={carouselSlides}
                shouldUseNewBrandBackground
                shouldUseTransparentBackground
                usePlainContainer={gwp9286ConfluenceAnchorUpdates}
            />
        );
    };

    if (isLoggedIn) {
        if (shouldShowLogMeIn) {
            customHeroSection = (
                <FeatureExposed
                    experimentKey="cfind-2801-get-paid-users-back-to-product"
                    group="Test"
                >
                    <HeroSection
                        confluenceSites={recentSites}
                        userProfileName={userProfileName}
                        hideExploreMore
                        loggedInPaid
                    />
                </FeatureExposed>
            );
        } else {
            customHeroSection = getLiftShiftHeroSectionBrandUpdate();
        }
    } else {
        // Logged out user
        customHeroSection = null;
    }

    return (
        <BanditsProvider
            product={BANDITS_PRODUCT.CONFLUENCE}
            keyword={pageProps.keyword}
            isExperimentEnabled={isCBSubAnchorExperimentEnabled}
            useCase={BANDITS_USE_CASE.ANCHOR_JOURNEY_SUB_ANCHOR}
            subAnchor={pageProps.subAnchor || ""}
            channel={pageProps.utmMedium || "paid-search"}
            utmContent={pageProps.utmContent || ""}
            utmCampaign={pageProps.utmCampaign || ""}
        >
            <FeatureExposed
                experimentKey="cfind-3090-paid-confluence-product-tour"
                group="Test"
            >
                <ConfluencePagePaidLpConflAnchor
                    {...pageProps}
                    pageId={pageId}
                    persona={"unknown" as PaidLandingPersonaTypes}
                    pageVariant="brandUpdate"
                    dataProductKey="confluence-AnchorLpConfCollaboration"
                    keyword={pageProps.keyword}
                    carouselHeroProps={carouselHeroProps}
                    customHeroSection={customHeroSection}
                />
            </FeatureExposed>
        </BanditsProvider>
    );
};
